// PowerSync DB wrapper — единственная точка получения инстанса БД и helper'ы
// для CRUD-операций над синкаемыми таблицами.
//
// Назначение:
//   - `getPowerSync()` (re-export из client.ts) — инициализирует WASM SQLite + OPFS
//   - `execute(sql, params)` — пробрасывает в локальную SQLite, PowerSync upload-queue
//     асинхронно пушит результат на /api/sync/powersync/write
//   - `insertRow` / `updateRow` / `deleteRow` — типизированные обёртки над execute,
//     генерируют SQL по объекту, добавляют timestamp поля автоматически
//
// Все ID — UUID v7 (`uuidv7()` из native пакета `uuidv7`).
import { uuidv7 } from "uuidv7";
import { getPowerSync } from "./client.js";

export {
	connectPowerSync,
	disconnectPowerSync,
	getPowerSync,
} from "./client.js";

/** Низкоуровневое выполнение SQL. Все мутации через PowerSync API. */
export async function execute(
	sql: string,
	params: ReadonlyArray<string | number | null> = [],
): Promise<void> {
	await getPowerSync().execute(sql, params as unknown[]);
}

/** Типизированный SELECT (read-only). Для реактивных компонентов — `usePowerSyncQuery`. */
export async function selectAll<T>(
	sql: string,
	params: ReadonlyArray<string | number | null> = [],
): Promise<T[]> {
	const result = await getPowerSync().getAll<T>(sql, params as unknown[]);
	return result;
}

/** Один ряд или null. */
export async function selectOne<T>(
	sql: string,
	params: ReadonlyArray<string | number | null> = [],
): Promise<T | null> {
	const row = await getPowerSync().getOptional<T>(sql, params as unknown[]);
	return row ?? null;
}

type Primitive = string | number | null | boolean;
type RowValues = Record<string, Primitive | undefined>;

function normalize(value: Primitive | undefined): string | number | null {
	if (value === undefined) return null;
	if (typeof value === "boolean") return value ? 1 : 0;
	return value;
}

/** INSERT с автогенерацией id (UUID v7) если не передан + created_at/updated_at. */
export async function insertRow(table: string, values: RowValues): Promise<string> {
	const id = (values.id as string | undefined) ?? uuidv7();
	const nowIso = new Date().toISOString();
	const merged: RowValues = {
		id,
		created_at: nowIso,
		updated_at: nowIso,
		...values,
	};
	const columns = Object.keys(merged);
	const placeholders = columns.map(() => "?").join(", ");
	const params = columns.map((c) => normalize(merged[c]));
	const sql = `INSERT INTO ${table} (${columns.join(", ")}) VALUES (${placeholders})`;
	await execute(sql, params);
	return id;
}

/** PATCH — частичное обновление по id. Обновляет updated_at автоматически. */
export async function updateRow(table: string, id: string, patch: RowValues): Promise<void> {
	const merged: RowValues = { ...patch, updated_at: new Date().toISOString() };
	const cols = Object.keys(merged);
	if (cols.length === 0) return;
	const assigns = cols.map((c) => `${c} = ?`).join(", ");
	const params = [...cols.map((c) => normalize(merged[c])), id];
	const sql = `UPDATE ${table} SET ${assigns} WHERE id = ?`;
	await execute(sql, params);
}

/** Soft-delete (UPDATE deleted_at). Hard-delete делается через `deleteRowHard`. */
export async function softDeleteRow(table: string, id: string): Promise<void> {
	await execute(`UPDATE ${table} SET deleted_at = ?, updated_at = ? WHERE id = ?`, [
		new Date().toISOString(),
		new Date().toISOString(),
		id,
	]);
}

/** Hard DELETE. Использовать осторожно — для записей без soft-delete семантики. */
export async function deleteRowHard(table: string, id: string): Promise<void> {
	await execute(`DELETE FROM ${table} WHERE id = ?`, [id]);
}
